import cx from 'classnames';
import { t } from 'i18next';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { PossibleStreamTooltip } from '../../../../../../../shared/frontend/components/presentational/streamPreview';
import { Stack } from '../../../../../../../shared/frontend/components/stack/Stack';
import { DataTable } from '../../../../../../../shared/frontend/components/tables/dataTable/DataTable';
import { IDataTableProps, TColumn } from '../../../../../../../shared/frontend/components/tables/types';
import { asset } from '../../../../../../../shared/frontend/services/assetResolverService';
import { namedRoutes, resolveRoutePath } from '../../../bundles/routes';
import { Link, PossibleLink } from '../../../../../../../shared/frontend/components/link';
import { RecruitmentProfileBadge } from '../../../shared/recruitment/components/RecruitmentProfileBadge';

type TData = any;

interface ITopILvlGuildsTableProps extends Omit<IDataTableProps<TData>, 'columns'> {}

export const TopILvlGuildsTable = ({ className, ...props }: ITopILvlGuildsTableProps) => {
  const navigate = useNavigate();

  const columns: TColumn<TData>[] = useMemo(
    () => [
      {
        accessor: 'guild',
        header: 'Guild',
        cellRender: ({ data }) => {
          if (!data) {
            return '-';
          }

          let faction;
          const stream = data?.stream?.stream;
          const streamCount = data?.stream?.count ?? 0;

          if (data.faction === 0) {
            faction = 'alliance';
          } else if (data.faction === 1) {
            faction = 'horde';
          }

          const handleClickThumbnail = () => {
            navigate({
              pathname: resolveRoutePath(namedRoutes.GuildRoster, {
                region: data.region.slug,
                realm: data.realm.slug,
                guild: data.name
              }),
              hash: '#mode=streamers'
            });
          };

          const recruitmentProfiles = data.recruitmentProfiles;

          return (
            <Stack alignItems="center" spacingInRems={5}>
              {(faction || data.logoUrl) && (
                <div className="image-wrapper-height-full" style={{ height: 24, flexShrink: 0 }}>
                  <img src={data.logoUrl || asset(`/images/site/${faction}_icon4.png`)} alt={`${data.name} logo`} />
                </div>
              )}

              <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                <Stack style={{ width: '100%' }} alignItems="center" spacingInRems={0}>
                  <span
                    className={cx(
                      'slds-truncate',
                      'd-inline-block',
                      'slds-text-heading--small',
                      faction && `rio-faction-color--${faction}`,
                      streamCount > 0 && 'streaming-now'
                    )}
                  >
                    <PossibleStreamTooltip
                      clickable
                      stream={stream}
                      count={streamCount}
                      onClickThumbnail={handleClickThumbnail}
                    >
                      <PossibleLink
                        allowLink={!data.anonymized}
                        to={resolveRoutePath(namedRoutes.GuildDetails, {
                          region: data.region.slug,
                          realm: data.realm.slug,
                          guild: data.name
                        })}
                        className={'!color-inherit'}
                      >
                        {data.name}
                      </PossibleLink>
                    </PossibleStreamTooltip>
                  </span>

                  {recruitmentProfiles && recruitmentProfiles.length > 0 && (
                    <RecruitmentProfileBadge
                      link={resolveRoutePath(namedRoutes.GuildRecruitmentPage, {
                        region: data.region.slug,
                        realm: data.realm.slug,
                        guild: data.name
                      })}
                      className={`rio-faction-color--${data.faction} slds-text-title--bold slds-m-left--xx-small`}
                      profile={recruitmentProfiles[0].recruitment_profile_id}
                    />
                  )}
                </Stack>

                <div className="slds-text-body--small">
                  <Link
                    className="rio-realm-link"
                    to={resolveRoutePath(namedRoutes.RegionHome, { region: data.region.slug })}
                  >
                    ({t(`regions:${data.region.slug}.short-name`)})
                  </Link>{' '}
                  <Link
                    className="rio-realm-link"
                    to={resolveRoutePath(namedRoutes.RealmHome, { region: data.region.slug, realm: data.realm.slug })}
                  >
                    {data.realm.name}
                  </Link>
                </div>
              </div>
            </Stack>
          );
        }
      },
      {
        accessor: 'itemLevelEquipped',
        header: 'Item Level',
        align: 'right',
        width: '80px',
        cellRender: ({ data }) => {
          return (
            <Link
              className="rio-realm-link slds-text-body--large"
              to={resolveRoutePath(namedRoutes.GuildDetails, {
                region: data.region.slug,
                realm: data.realm.slug,
                guild: data.name
              })}
            >
              {data.itemLevelEquipped?.all?.top20?.toFixed(2)}
            </Link>
          );
        }
      }
    ],
    []
  );

  return (
    <DataTable hideHeader columns={columns} className={cx('slds-table rio-rankings-table', className)} {...props} />
  );
};
